"use client";
import Link from 'next/link';
import Layout from '@/components/Layout';
import usePageTitle from '@/hooks/usePageTitle';
import { siteConfig } from '@/lib/config';
import { PageHero } from '@/components/sections/PageHero';

export default function AccessibilityStatementPage() {
  usePageTitle('Accessibility Statement — Laurel Web Co.');

  return (
    <Layout>
      <PageHero
        ariaLabel="Accessibility statement header"
        title="Accessibility Statement"
        subtitle="Commitment to digital inclusion for all."
      />

      <section className="bg-card py-24" aria-label="Accessibility statement details">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Our Commitment</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              Laurel Web Co. is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Conformance Status</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              This website conforms to WCAG 2.1 Level AA. We actively test and remediate any accessibility barriers.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Technical Specifications</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              This website is built using HTML5, CSS3, ARIA landmarks and roles, and follows semantic HTML practices. It is designed to work with assistive technologies including screen readers, screen magnifiers, and keyboard-only navigation.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Supported Technologies</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              We test with NVDA and VoiceOver screen readers, across Chrome, Firefox, Safari, and Edge. The site is fully functional with keyboard-only navigation.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Known Limitations</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              We are not currently aware of any accessibility limitations on this website. If you encounter any barriers, please let us know.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Feedback</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              We welcome your feedback on the accessibility of this website. If you encounter any barriers, please contact us through our <Link href="/contact" className="text-bloom-green-deep font-semibold hover:underline focus-bloom rounded">contact form</Link> or email us directly at <a href={`mailto:${siteConfig.email}`} className="text-bloom-green-deep font-semibold hover:underline">{siteConfig.email}</a>.
            </p>
            <div className="mt-4 p-4 bg-bloom-cream border border-bloom-border rounded-lg text-sm font-sans leading-relaxed">
              <p className="font-bold mb-1">{siteConfig.name}</p>
              <p>{siteConfig.address.careOf}</p>
              <p>{siteConfig.address.street}</p>
              <p>{siteConfig.address.city}, {siteConfig.address.state} {siteConfig.address.zip}</p>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Assessment Approach</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              We assess our website accessibility through self-evaluation using automated tools including axe, WAVE, and Google Lighthouse, supplemented by manual keyboard and screen reader testing.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-serif text-xl font-normal text-bloom-charcoal mb-3">Date Reviewed</h2>
            <p className="font-sans text-bloom-charcoal leading-[1.7]">
              This statement was last updated on March 1, 2026.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
